import { Flex, Table, Typography } from "antd";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useTableColumns } from "~hooks/useTableColumns";
import { AddEntityLink, AddPersonLink, AddVenueLink, SeasonFilters } from "~pages/Dashboard/components";
import { loadFromLocalStorage } from "~utilities/loadFromLocalStorage";
import { saveToLocalStorage } from "~utilities/saveToLocalStorage";
import useGetSeasonsTableColumns from "./hooks/useGetSeasonsTableColumns";
import type { SeasonFilterParams } from "./types";
import { extractSeasonsFromScope, filterSeasons, getCompetitionOptions } from "./utilities";

const DashboardPage = () => {
  const { sport, organizationId, customerId } = useParams();
  const filtersLocalStorageKey = `${sport}.tables.settings.filters.dashboard`;
  const columnSettingsLocalStorageKey = `${sport}.tables.settings.columns.dashboard`;
  const sorterLocalStorageKey = `${sport}.tables.settings.sort.dashboard`;
  const { uiConfiguration } = useSportConfiguration();
  const tableConfiguration = uiConfiguration.pages.dashboard.tableColumnGroups;
  const initialFilters = useMemo(() => loadFromLocalStorage(filtersLocalStorageKey), [filtersLocalStorageKey]);
  const [filters, setFilters] = useState<SeasonFilterParams>(
    initialFilters || {
      searchText: "",
      competitionId: null,
      status: null,
      gender: null,
      ageGroup: null,
    },
  );
  const navigate = useNavigate();
  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const columns = useGetSeasonsTableColumns();

  const { columnsToShow, setSortColumn } = useTableColumns({
    columnSettingsConfig: {
      columnSettingsLocalStorageKey,
      tableConfiguration,
    },
    columnSortConfig: {
      sorterLocalStorageKey: sorterLocalStorageKey,
      defaultSorter: {
        columnKey: "year",
        order: "descend",
      },
    },
    tableColumns: columns,
  });

  const allSeasons = useMemo(
    () => (scopeData ? extractSeasonsFromScope(scopeData.scope.leagueTree, scopeData.resources) : []),
    [scopeData],
  );
  const filteredSeasons = useMemo(() => filterSeasons(allSeasons, filters), [allSeasons, filters]);
  const competitionOptions = useMemo(() => getCompetitionOptions(scopeData?.resources?.competitions || {}), [scopeData]);

  const navigateToSeason = (seasonId: string) => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`);

  const handleFilterChange = (filterType: keyof SeasonFilterParams, value: string | null) => {
    const newFilters = { ...filters, [filterType]: value };
    saveToLocalStorage(filtersLocalStorageKey, JSON.stringify(newFilters));
    setFilters(newFilters);
  };

  return (
    <Flex vertical gap={8}>
      <Typography.Title level={4} style={{ fontWeight: 400 }}>
        <FormattedMessage id="quickLinks" />
      </Typography.Title>
      <Flex wrap gap={20}>
        <AddEntityLink />
        <AddPersonLink />
        <AddVenueLink />
      </Flex>
      <Typography.Title level={4} style={{ fontWeight: 400 }}>
        <FormattedMessage id="seasons" />
      </Typography.Title>
      <SeasonFilters competitionOptions={competitionOptions} values={filters} onFilterChange={handleFilterChange} disabled={isLoading} />
      <Table
        loading={isLoading}
        size="small"
        onRow={(season) => {
          return {
            onClick: () => navigateToSeason(season.seasonId),
          };
        }}
        columns={columnsToShow}
        rowKey="seasonId"
        rowClassName="is-clickable"
        dataSource={filteredSeasons}
        onChange={(_, __, sorter) => setSortColumn(sorter)}
        pagination={{
          pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
          defaultPageSize: 50,
          showSizeChanger: true,
          position: ["bottomCenter"],
        }}
      />
    </Flex>
  );
};

export default DashboardPage;
